import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogLimitedCostTypeDataFilterInput } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimcCalculationLimitedCostTypesGql } from '../../../bimCalculationLimitedQueries';

interface IUseBimGetCalculationLimitedCostTypes {
    limitedId?: number;
    calculationId?: number;
    withEffect?: boolean;
}

const defaultVariables = {
    order: [
        {
            name: SortEnumType.Asc,
        },
    ],
    take: 50,
};

export const useBimGetCalculationLimitedCostTypes = (props: IUseBimGetCalculationLimitedCostTypes) => {
    const { limitedId, calculationId, withEffect = true } = props;
    const dispatch = useDispatch();

    const [page, setPage] = useState(0);

    const requestId = useMemo(() => `bimcCalculationLimitedCostTypes${limitedId || ''}`, [limitedId]);

    const variables = useMemo(
        () => ({
            calculationId: calculationId as number,
            limitedCostId: limitedId,
            order: [{ name: SortEnumType.Asc }],
        }),
        [calculationId, limitedId],
    );

    const [resetStoreData] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            resetStoreData(undefined);
        },
        [resetStoreData],
    );

    const [bimcCalculationLimitedCostTypesData, status, , bimcCalculationLimitedCostTypesMakeRequest] = useRequest({
        requestId,
        query: bimcCalculationLimitedCostTypesGql,
        withEffect: Boolean(withEffect && calculationId),
        variables,
    });

    const items = useMemo(
        () => bimcCalculationLimitedCostTypesData?.bimcCalculationLimitedCostTypes?.items ?? [],
        [bimcCalculationLimitedCostTypesData?.bimcCalculationLimitedCostTypes?.items],
    );

    const hasNextPage = useMemo(
        () => bimcCalculationLimitedCostTypesData?.bimcCalculationLimitedCostTypes?.pageInfo?.hasNextPage,
        [bimcCalculationLimitedCostTypesData?.bimcCalculationLimitedCostTypes?.pageInfo?.hasNextPage],
    );

    const requestPage = useCallback(
        (pageIndex: number, searchString?: string) => {
            if (status === RequestStatus.FETCHING) {
                return;
            }

            const where: CatalogLimitedCostTypeDataFilterInput | undefined = searchString
                ? {
                      name: { containsi: searchString },
                  }
                : undefined;

            setPage(searchString ? 0 : pageIndex + 1);

            const input = {
                ...variables,
                where,
                order: defaultVariables.order,
                take: searchString ? undefined : defaultVariables.take,
                skip: searchString ? 0 : pageIndex * defaultVariables.take,
            };

            dispatch(bimcCalculationLimitedCostTypesMakeRequest(input));
        },
        [dispatch, status, bimcCalculationLimitedCostTypesMakeRequest, variables],
    );

    const requestSearchByName = useCallback((searchString?: string) => requestPage(0, searchString), [requestPage]);

    const onNextPageRequest = useCallback(() => {
        requestPage(page);
    }, [page, requestPage]);

    return {
        items,
        status,
        onNextPageRequest,
        requestSearchByName,
        hasNextPage,
    };
};
